import { useEffect, useState, useMemo } from "react"
import toast from "react-hot-toast"
import { SquareCheckBig } from "lucide-react"
import { Button, Dropdown } from "react-bootstrap"

import {
  useClearAdminNotificationsMutation,
  useFetchAdminQuery,
  useFetchAdminNotificationsQuery,
  useAdminNotificationsReadMutation,
  useDeleteAdminNotificationMutation
} from "../../../app/adminApiSlice"
import Spinner from "../../../components/Spinner"
import DisplayErrorMessage from "../../../components/DisplayErrorMessage"
import FormattedNotification from "../../../components/notifications/FormattedNotification"
import ButtonWithTooltip from "../../../components/buttons/ButtonWithTooltip"

const NotificationsPage = () => {
  const [deleteAdminNotification] = useDeleteAdminNotificationMutation()
  const [clearAdminNotifications] = useClearAdminNotificationsMutation()
  const { data } = useFetchAdminQuery()
  const { data: notifications, isSuccess, isLoading, isError, error } = useFetchAdminNotificationsQuery()
  const [adminNotificationsRead] = useAdminNotificationsReadMutation()

  const [view, setView] = useState("notifications")
  const [showDropdown, setShowDropdown] = useState(false)

  // Clear notifications when the component mounts
  useEffect(() => {
    if (data?.notifications > 0) {
      clearAdminNotifications()
    }
  }, [])

  // Calculate unread notifications
  const { unreadRequests, unreadMessages } = useMemo(() => {
    if (!notifications) return { unreadRequests: [], unreadMessages: [] }

    const unreadRequests = Object.entries(notifications?.requests || {})
      .filter(([_, request]) => !request.read)
      .map(([id]) => id)

    const unreadMessages = Object.entries(notifications?.messages || {})
      .filter(([_, message]) => !message.read)
      .map(([id]) => id)

    return { unreadRequests, unreadMessages }
  }, [notifications])

  const hasUnread = unreadRequests.length > 0 || unreadMessages.length > 0

  const markAllRead = () => {
    if (hasUnread) {
      adminNotificationsRead({ requests: unreadRequests, messages: unreadMessages })
    }
  }

  const markOneRead = (id, category) => {
    if (category === "requests") {
      adminNotificationsRead({ requests: [id], messages: [] })
    } else if (category === "messages") {
      adminNotificationsRead({ requests: [], messages: [id] })
    }
  }

  const handleDeleteNotification = async (data) => {
    try {
      await deleteAdminNotification(data).unwrap()

    } catch (e) {
      console.log(e)
      toast.error("Error deleting notification")
    }
  }

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "An error occurred while fetching notifications")
    return <DisplayErrorMessage>Error fetching notifications</DisplayErrorMessage>
  }
  if (!isSuccess || !data) return null

  const requestsArray = Object.entries(notifications?.requests ?? {}).map(([id, requestData]) => ({
    id,
    category: "requests",
    ...requestData
  }))

  const messagesArray = Object.entries(notifications?.messages ?? {}).map(([id, messageData]) => ({
    id,
    category: "messages",
    ...messageData
  }))

  const mergedArray = [...requestsArray, ...messagesArray]

  const viewMap = {
    notifications: mergedArray,
    requests: requestsArray,
    messages: messagesArray
  }

  const chosenArray = viewMap[view] ?? mergedArray

  const sortedArray = chosenArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

  const listContent = sortedArray.length > 0 ? (
    <ul className="w-100 list-unstyled">
      {sortedArray.map((content) => {
        return (
          <FormattedNotification
            key={content.id}
            data={{
              ...content
            }}
            deleteNotification={() => handleDeleteNotification({ docId: content.id, category: content.category })}
            markRead={() => markOneRead(content.id, content.category)}
          />
        )
      })}
    </ul>
  ) : (
    <p>No {view} to show</p>
  )

  const currentClass = "fw-semibold rounded-3 bg-primary bg-opacity-10 text-primary"
  const inactiveClass = "border-0 fw-semibold rounded-3 text-muted custom-hover"

  return (
    <div
      className="d-flex flex-column align-items-center shadow-sm rounded-4 pb-4 pt-3 px-2 p-sm-4 bg-white"
      style={{ maxWidth: "100%", width: "680px" }}
    >
      <div className="position-relative w-100 mb-4">
        <h3 className="fw-bold text-center mb-0 mt-2">Notifications</h3>

        {/* Mark all as read button */}
        {hasUnread && (
          <ButtonWithTooltip text="Mark all as read">
            <Button onClick={markAllRead} variant="" className="ms-auto border-0 p-2 me-2 position-absolute top-0 end-0"><SquareCheckBig /></Button>
          </ButtonWithTooltip>
        )}
      </div>

      {/* Change view buttons */}
      <div className="justify-content-start w-100 gap-3 ps-2 d-none d-sm-flex">
        <Button onClick={() => setView("notifications")} variant="" className={view === "notifications" ? currentClass : inactiveClass}>All</Button>
        <Button onClick={() => setView("messages")} variant="" className={view === "messages" ? currentClass : inactiveClass}>Messages</Button>
        <Button onClick={() => setView("requests")} variant="" className={view === "requests" ? currentClass : inactiveClass}>Requests</Button>
      </div>

      {/* Dropdown menu for mobile */}
      <div className="d-flex w-100 gap-3 ps-2 d-sm-none">
        <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)} drop="down-end">
          <Dropdown.Toggle
            variant=''
            className={`border-0 ${currentClass} px-3 mb-2`}
          >
            View
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow border-0 px-2">
            <Dropdown.Item onClick={() => setView("notifications")} className={`${view === "notifications" && currentClass} ${inactiveClass} custom-hover mb-1`}>
              All
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setView("messages")} className={`${view === "messages" && currentClass} ${inactiveClass} custom-hover mb-1`}>
              Messages
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setView("requests")} className={`${view === "requests" && currentClass} ${inactiveClass} custom-hover`}>
              Requests
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <hr className="w-100 text-secondary mt-1" />
      {listContent}
    </div>
  )
}

export default NotificationsPage

